import { Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import { Placeholder } from "../components/Placeholder";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { eventTimestamp } from "../lib/events";
import { usePublishedEventsState } from "../lib/publishedEvents";
import { getDepartment } from "../lib/departments";
import { useScrollPastHero } from "../lib/useScrollPastHero";

/** A title this long is a sentence rather than a name, so the banner sets it like a description (see .event-title.is-long). */
const LONG_TITLE_CHARS = 60;

export default function EventDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { events: allEvents, loading } = usePublishedEventsState();
  const e = allEvents.find((x) => x.slug === slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (loading) return null;

  if (!e) {
    return (
      <section className="event-hero">
        <div className="container">
          <div className="event-eyebrow">Event not found</div>
          <h1 className="event-title">No event matches "{slug}"</h1>
          <p className="event-summary">
            The event you're looking for may have been moved or renamed. Browse all events for what is currently published.
          </p>
          <Link className="btn-link" to={ROUTES.events}>← Back to events</Link>
        </div>
      </section>
    );
  }

  const dept = getDepartment(e.departmentCode);
  const related = allEvents
    .filter((x) => x.slug !== e.slug)
    .sort((a, b) => eventTimestamp(b) - eventTimestamp(a))
    .slice(0, 3);

  return (
    <article className="event-page">
      <ContextBar
        visible={scrolledPast}
        tag={`${e.tag.toUpperCase()} · ${e.dateBig.day} ${e.dateBig.month}`}
        title={e.title}
        back={{ label: "All events", to: ROUTES.events }}
      />
      {/* ─── Banner: the facts, with the event's photo beside them ─ */}
      <section
        className={"event-hero " + (e.status === "past" ? "is-past" : "is-upcoming") + (e.media ? " has-photo" : "")}
        ref={heroRef}
      >
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden="true">/</span>
            <Link to={ROUTES.events}>Events</Link>
            <span aria-hidden="true">/</span>
            <span>{e.tag}</span>
          </nav>

          <div className="event-hero-grid">
            <div className="event-hero-text">
              <div className="event-eyebrow">
                <span className="event-date" aria-hidden="true">{e.dateBig.day} {e.dateBig.month} {e.dateBig.year}</span>
                <span className={"event-pill pill-" + e.tag.toLowerCase()}>{e.tag}</span>
                <span className="event-status">{e.status === "upcoming" ? "Upcoming" : "Archived"}</span>
              </div>
              <h1 className={"event-title" + (e.title.length > LONG_TITLE_CHARS ? " is-long" : "")}>{e.title}.</h1>
              {!e.summaryIsDescription && <p className="event-summary">{e.summary}</p>}

              <dl className="event-meta-strip">
                <div>
                  <dt>When</dt>
                  <dd>{e.dateLine}{e.time ? ` · ${e.time}` : ""}</dd>
                </div>
                <div>
                  <dt>Where</dt>
                  <dd>{e.venue}</dd>
                </div>
                {dept && (
                  <div>
                    <dt>Owned by</dt>
                    <dd>
                      <Link to={`${ROUTES.department}/${dept.slug}`}>{dept.code} · {dept.name}</Link>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {e.media && (
              <figure className="event-hero-photo">
                {/* The box takes the photo's own shape, so nothing is cropped away. */}
                <Figure
                  media={e.media}
                  fallbackLabel={e.hero}
                  kind="dark"
                  aspect={e.media.width && e.media.height ? `${e.media.width}/${e.media.height}` : "4/3"}
                  sizes="(max-width: 900px) 100vw, 500px"
                  priority
                />
              </figure>
            )}
          </div>
        </div>
      </section>

      {/* ─── Body + sticky meta column ─────────────────────────── */}
      <section className="event-shell">
        <div className="container">
          <div className="event-shell-grid">
            <div className="event-body">
              {e.body.map((b, i) => (
                <Fragment key={i}>
                  {b.heading && <h2>{b.heading}</h2>}
                  {b.text && <p>{b.text}</p>}
                  {b.list && (
                    <ul className="event-list">
                      {b.list.map((li, j) => (
                        <li key={j}>{li}</li>
                      ))}
                    </ul>
                  )}
                </Fragment>
              ))}

              {e.schedule && (
                <>
                  <h2>Schedule</h2>
                  <ol className="event-schedule">
                    {e.schedule.map((s, i) => (
                      <li key={i}>
                        <span className="es-time">{s.time}</span>
                        <span className="es-line" aria-hidden="true" />
                        <span className="es-body">
                          <span className="es-what">{s.what}</span>
                          {s.who && <span className="es-who">{s.who}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                </>
              )}

              {e.organizers && (
                <>
                  <h2>Organisers</h2>
                  <div className="event-organisers">
                    {e.organizers.map((o, i) => (
                      <span key={i}>{o}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <aside className="event-meta-card" aria-label="Event details">
              <div className="emc-block">
                <div className="emc-label">Date</div>
                <div className="emc-value">{e.dateLine}</div>
              </div>
              <div className="emc-block">
                <div className="emc-label">Time</div>
                <div className="emc-value">{e.time}</div>
              </div>
              <div className="emc-block">
                <div className="emc-label">Venue</div>
                <div className="emc-value">{e.venue}</div>
              </div>
              {e.registrationDeadline && (
                <div className="emc-block">
                  <div className="emc-label">Registration closes</div>
                  <div className="emc-value">{e.registrationDeadline}</div>
                </div>
              )}
              {e.status === "upcoming" && e.registrationUrl && (
                <a href={e.registrationUrl} className="emc-cta">
                  Register <span aria-hidden="true">→</span>
                </a>
              )}
              {e.status === "past" && (
                <div className="emc-archived">This event has concluded.</div>
              )}
              <div className="emc-share">
                <span>Share</span>
                <span className="emc-share-line" aria-hidden="true" />
                <a href="#" aria-label="Share via mail">@</a>
                <a href="#" aria-label="Share to twitter">x</a>
                <a href="#" aria-label="Copy link">↗</a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ─── Related ─────────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More on the calendar</div>
              <h2>Other events</h2>
            </div>
            <div className="er-grid">
              {related.map((r) => (
                <Link key={r.slug} to={`${ROUTES.events}/${r.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{r.dateBig.day}</span>
                    <span className="er-month">{r.dateBig.month}</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{r.tag}</span>
                    <h3>{r.title}</h3>
                    <span className="er-venue">{r.venue}</span>
                  </div>
                  <span className="er-arr" aria-hidden="true">→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
